import { h } from 'preact'
import { useState } from 'preact/hooks'
import FormInput from '../elements/FormInput'
import initializeForm from '../utilities/initializeForm'
import { formFieldGroups, formSchema, initialValues } from './config'

import sharedStyles from '../sharedStyles.module.scss'
import formatNumber from '../../../utilities/formatNumber'

import ChartContainer from '../../charts/ChartContainer/ChartContainer'
import BarChart from '../../charts/BarChart'

export default function CashFlowCalculator({ formName }) {
  const { formValues, formErrors, handleInputChange, validateForm, resetForm } =
    initializeForm({
      initialValues,
      formSchema,
    })

  const [view, setView] = useState('form')
  const [data, setData] = useState(null)

  function renderGroupInputs(groupID) {
    return Object.entries(formFieldGroups[groupID]).map(([key, label]) => {
      const id = `${formName}-${groupID}-${key}`
      const formKey = `${groupID}-${key}`
      return (
        <FormInput
          key={id}
          id={id}
          identifier={formKey}
          input={{
            label: label,
            class: 'Currency',
            type: 'text',
          }}
          error={formErrors?.[formKey]}
          value={formValues?.[formKey]}
          handleInputChange={handleInputChange}
          displayBlock
        />
      )
    })
  }

  function calculateGroupTotal(groupId) {
    return Object.keys(formFieldGroups[groupId]).reduce((num, key) => {
      const valueKey = `${groupId}-${key}`
      if (formValues[valueKey]) {
        return (num += Number(formValues[valueKey].replace(/[^0-9.-]/g, '')))
      }
      return num
    }, 0)
  }

  function renderDisplayTotal(total) {
    return isNaN(total)
      ? 'One of the fields above is not a number.'
      : Number(total)
      ? `$${formatNumber(total)}`
      : null
  }

  const livingExpenseTotal = calculateGroupTotal('living')
  const transportationExpensesTotal = calculateGroupTotal('transportation')
  const debtPaymentTotal = calculateGroupTotal('debtPayment')
  const commitedSavingsTotal = calculateGroupTotal('commitedSavings')
  const dailyLivingExpensesTotal = calculateGroupTotal('dailyLivingExpenses')
  const personalRelatedExpensesTotal = calculateGroupTotal(
    'personalRelatedExpenses',
  )
  const entertainmentTotal = calculateGroupTotal('entertainment')
  const recreationalTotal = calculateGroupTotal('recreational')
  const duesSubscriptionsTotal = calculateGroupTotal('duesSubscriptions')
  const healthExpensesTotal = calculateGroupTotal('healthExpenses')
  const vacationExpensesTotal = calculateGroupTotal('vacationExpenses')
  const otherExpensesTotal = calculateGroupTotal('otherExpenses')

  const allExpensesTotal = Object.keys(formFieldGroups).reduce((num, key) => {
    const sectionTotal = calculateGroupTotal(key)

    const total = num + sectionTotal
    return total
  }, 0)

  const disposableMonthlyIncome = Number(formValues?.monthlyIncome)
    ? Number(formValues?.monthlyIncome) - allExpensesTotal
    : allExpensesTotal * -1

  function submit() {
    validateForm().then(() => {
      const chartData = [
        {
          year: 1,
          value: Number(formValues.monthlyIncome),
          fill: '#035595',
          key: 'income',
          label: 'Month Income',
        },
        {
          year: 2,
          value: Number(allExpensesTotal),
          fill: '#a9ddf7',
          key: 'expenses',
          label: 'Month Expenses',
        },
        {
          year: 3,
          value: Number(disposableMonthlyIncome),
          fill: '#e0e0e0',
          key: 'disposable',
          label: 'Disposable Month Income',
        },
      ]

      setData(chartData)
      setView('results')
    })
  }

  if (view === 'results') {
    return (
      <ChartContainer
        reset={() => {
          setView('form')
          setData(null)
          resetForm()
        }}
        edit={() => setView('form')}
      >
        <BarChart data={data} hideXAxis showToggles />
      </ChartContainer>
    )
  }

  return (
    <div>
      <form
        className={sharedStyles.Form + ' ' + sharedStyles.FlexForm}
        onSubmit={e => {
          e.preventDefault()
          submit()
        }}
      >
        {/* COLUMN ONE */}
        <div className={sharedStyles.FormColumn}>
          <h4>Home/Living Expenses</h4>
          {renderGroupInputs('living')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Home/Living Expenses</p>
            <p>{renderDisplayTotal(livingExpenseTotal)}</p>
          </div>

          <h4>Transportation Expenses</h4>
          {renderGroupInputs('transportation')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Transportation Expenses</p>
            <p>{renderDisplayTotal(transportationExpensesTotal)}</p>
          </div>

          <h4>Debt Payments</h4>
          {renderGroupInputs('debtPayment')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Debt Obligations</p>
            <p>{renderDisplayTotal(debtPaymentTotal)}</p>
          </div>

          <h4>Commited Savings</h4>
          {renderGroupInputs('commitedSavings')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Savings</p>
            <p>{renderDisplayTotal(commitedSavingsTotal)}</p>
          </div>

          <h4>Daily Living Expenses</h4>
          {renderGroupInputs('dailyLivingExpenses')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Daily Living Expenses</p>
            <p>{renderDisplayTotal(dailyLivingExpensesTotal)}</p>
          </div>

          <h4>Personal Related Expenses</h4>
          {renderGroupInputs('personalRelatedExpenses')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Personal Expenses</p>
            <p>{renderDisplayTotal(personalRelatedExpensesTotal)}</p>
          </div>
        </div>
        {/*  COLUMN TWO  */}
        <div className={sharedStyles.FormColumn}>
          <h4>Entertainment Expenses</h4>
          {renderGroupInputs('entertainment')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Entertainment Expenses</p>
            <p>{renderDisplayTotal(entertainmentTotal)}</p>
          </div>

          <h4>Recreation Expenses</h4>
          {renderGroupInputs('recreational')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Recreation Expenses</p>
            <p>{renderDisplayTotal(recreationalTotal)}</p>
          </div>

          <h4>Dues/Subscriptions/Fees</h4>
          {renderGroupInputs('duesSubscriptions')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Dues/Subscription Expenses</p>
            <p>{renderDisplayTotal(duesSubscriptionsTotal)}</p>
          </div>

          <h4>Health Related Expenses</h4>
          {renderGroupInputs('healthExpenses')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Health Related Expenses</p>
            <p>{renderDisplayTotal(healthExpensesTotal)}</p>
          </div>

          <h4>Vacation Expenses</h4>
          {renderGroupInputs('vacationExpenses')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Vacation Expenses</p>
            <p>{renderDisplayTotal(vacationExpensesTotal)}</p>
          </div>

          <h4>Other Expenses</h4>
          {renderGroupInputs('otherExpenses')}
          <div className={sharedStyles.SectionTotal}>
            <p>Monthly Other Expenses</p>
            <p>{renderDisplayTotal(otherExpensesTotal)}</p>
          </div>
          <div className={sharedStyles.SectionTotal}>
            <p>TOTAL Monthly Expenses</p>
            <p>{renderDisplayTotal(allExpensesTotal)}</p>
          </div>
          <h4>Summary</h4>
          <FormInput
            id={`${formName}-income-monthlyIncome`}
            identifier="monthlyIncome"
            input={{
              label: 'Total Monthly Income',
              class: 'Currency',
              type: 'text',
            }}
            error={formErrors?.monthlyIncome}
            value={formValues?.monthlyIncome}
            handleInputChange={handleInputChange}
            displayBlock
          />
          <div className={sharedStyles.SectionTotal}>
            <p>TOTAL Monthly Expenses (carried over from same above field)</p>
            <p>{renderDisplayTotal(allExpensesTotal)}</p>
          </div>

          <div className={sharedStyles.SectionTotal}>
            <p>Disposable Monthly Income</p>
            <p>{renderDisplayTotal(disposableMonthlyIncome)}</p>
          </div>

          <button className="button dark">Calculate</button>
        </div>
      </form>
    </div>
  )
}
